import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Search, Sparkles, Star, Cross, Clock, Trash2 } from 'lucide-react';
import { getSearchHistory, getDefaultSuggestions, clearSearchHistory } from '../utils/searchHistory';

export const HeroSection = ({ onSearch, isLoading }) => {
  const { t, language } = useLanguage();
  const [name, setName] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);

  // Load search history on mount and when language changes
  useEffect(() => {
    const history = getSearchHistory();
    setRecentSearches(history);
  }, []);

  // Refresh history when a search is done (called from parent)
  const refreshHistory = () => {
    const history = getSearchHistory();
    setRecentSearches(history);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (name.trim()) {
      onSearch(name.trim());
      // Refresh history after a short delay to allow parent to save
      setTimeout(refreshHistory, 100);
    }
  };

  const handleSuggestionClick = (suggestionName) => {
    setName(suggestionName);
    onSearch(suggestionName);
    setTimeout(refreshHistory, 100);
  };

  const handleClearHistory = () => {
    clearSearchHistory();
    setRecentSearches([]);
  };

  // Use recent searches if available, otherwise show defaults
  const suggestions = recentSearches.length > 0 
    ? recentSearches 
    : getDefaultSuggestions(language);

  const hasHistory = recentSearches.length > 0;

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background with overlay */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1637548181728-8348e37da365?w=1920&q=80)',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/95 via-background/80 to-background" />
        <div className="absolute inset-0 bg-gradient-to-r from-background/60 via-transparent to-background/60" />
      </div>

      {/* Decorative elements */}
      <div className="absolute top-32 left-10 opacity-20 animate-float">
        <Star className="w-8 h-8 text-gold" />
      </div>
      <div className="absolute top-40 right-16 opacity-15 animate-float" style={{ animationDelay: '1s' }}>
        <Cross className="w-12 h-12 text-gold" />
      </div>
      <div className="absolute bottom-32 left-20 opacity-10 animate-float" style={{ animationDelay: '2s' }}>
        <Sparkles className="w-10 h-10 text-gold" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Ornamental top */}
        <div className="flex justify-center mb-6">
          <div className="flex items-center gap-4">
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-gold/50" />
            <Cross className="w-5 h-5 text-gold" />
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-gold/50" />
          </div>
        </div>

        {/* Title */}
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-foreground mb-6 leading-tight animate-fade-in">
          {t('hero.title')}
        </h1>

        {/* Subtitle */}
        <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          {t('hero.subtitle')}
        </p>

        {/* Search Form */}
        <form 
          onSubmit={handleSubmit}
          className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto mb-8 animate-fade-in-up"
          style={{ animationDelay: '0.4s' }}
        >
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('hero.placeholder')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="pl-12 h-14 text-lg bg-card/80 backdrop-blur-sm border-2 border-border hover:border-gold/50 focus:border-gold transition-colors rounded-xl"
              disabled={isLoading}
            />
          </div>
          <Button 
            type="submit" 
            variant="sacred" 
            size="xl"
            disabled={isLoading || !name.trim()}
            className="min-w-[200px]"
          >
            {isLoading ? (
              <>
                <div className="spinner-sacred w-5 h-5" />
                <span>{t('hero.searching')}</span>
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                <span>{t('hero.button')}</span>
              </>
            )}
          </Button>
        </form>

        {/* Recent searches / Suggestions */}
        <div className="animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
          <div className="flex items-center justify-center gap-2 mb-4">
            {hasHistory ? (
              <>
                <Clock className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {language === 'fr' ? 'Recherches récentes :' : 'Recent searches:'}
                </p>
                <button
                  onClick={handleClearHistory}
                  className="text-xs text-muted-foreground hover:text-destructive transition-colors flex items-center gap-1 ml-2"
                  title={language === 'fr' ? 'Effacer l\'historique' : 'Clear history'}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">
                {language === 'fr' ? 'Suggestions de prénoms :' : 'Name suggestions:'}
              </p>
            )}
          </div>
          
          {/* Two rows of suggestions */}
          <div className="flex flex-col gap-2 max-w-2xl mx-auto">
            {/* First row */}
            <div className="flex flex-wrap justify-center gap-2">
              {suggestions.slice(0, 5).map((suggestionName) => (
                <button
                  key={suggestionName}
                  onClick={() => handleSuggestionClick(suggestionName)}
                  disabled={isLoading}
                  className={`px-4 py-2 text-sm font-medium border rounded-full transition-all duration-300 disabled:opacity-50 ${
                    hasHistory 
                      ? 'text-foreground bg-card/80 hover:bg-card border-gold/30 hover:border-gold/60 hover:shadow-soft' 
                      : 'text-muted-foreground bg-card/60 hover:bg-card hover:text-foreground border-border/50 hover:border-gold/30'
                  }`}
                >
                  {suggestionName}
                </button>
              ))}
            </div>
            
            {/* Second row (if more than 5 suggestions) */}
            {suggestions.length > 5 && (
              <div className="flex flex-wrap justify-center gap-2">
                {suggestions.slice(5, 10).map((suggestionName) => (
                  <button
                    key={suggestionName}
                    onClick={() => handleSuggestionClick(suggestionName)}
                    disabled={isLoading}
                    className={`px-4 py-2 text-sm font-medium border rounded-full transition-all duration-300 disabled:opacity-50 ${
                      hasHistory 
                        ? 'text-foreground bg-card/80 hover:bg-card border-gold/30 hover:border-gold/60 hover:shadow-soft' 
                        : 'text-muted-foreground bg-card/60 hover:bg-card hover:text-foreground border-border/50 hover:border-gold/30'
                    }`}
                  >
                    {suggestionName}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-gold/30 flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-gold/50 rounded-full" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
